export type WidgetScreen =
  | "bubble"
  | "ask-email"
  | "conversation"
  | "write-message"
  | "done"
  | "closed";

export type WidgetApi = {
  getPresence(): Promise<{ agentsOnline: boolean }>;
  getConversation(): Promise<{ status: "open" | "closed" }>;
  openConversation(email: string): Promise<{ id: string }>;
  sendMessage(body: string): Promise<void>;
};

function isConversationClosed(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    (error as { code?: string }).code === "CONVERSATION_CLOSED"
  );
}

export function createWidgetSession(api: WidgetApi) {
  const session = {
    screen: "bubble" as WidgetScreen,
    agentsOnline: false,
    conversationId: null as string | null,
    conversationStatus: null as "open" | "closed" | null,
    async refreshPresence() {
      const presence = await api.getPresence();
      session.agentsOnline = presence.agentsOnline;
    },
    async restore() {
      if (!session.conversationId) return;
      const conversation = await api.getConversation();
      session.conversationStatus = conversation.status;
      session.screen = conversation.status === "closed" ? "closed" : "conversation";
    },
    open() {
      if (session.conversationStatus === "closed" && session.conversationId) {
        session.screen = "closed";
        return;
      }
      if (session.conversationId) {
        session.screen = "conversation";
        return;
      }
      session.screen = "ask-email";
    },
    close() {
      session.screen = "bubble";
    },
    startNew() {
      session.conversationId = null;
      session.conversationStatus = null;
      session.screen = "ask-email";
    },
    async submitEmail(email: string) {
      const conversation = await api.openConversation(email);
      session.conversationId = conversation.id;
      session.conversationStatus = "open";
      session.screen = session.agentsOnline ? "conversation" : "write-message";
    },
    async send(body: string) {
      try {
        await api.sendMessage(body);
      } catch (error) {
        if (isConversationClosed(error)) {
          session.conversationStatus = "closed";
          session.screen = "closed";
          return;
        }
        throw error;
      }
      if (!session.agentsOnline) {
        session.screen = "done";
      }
    },
  };
  return session;
}
